import { timestamp, uuid } from "@/interfaces";

export type PurchaseOrder = object;

export type AppointmentClockData = {
  time: string; // ISO timestamp
  photoUrl: string; // Supabase storage URL
  lat?: number;
  lng?: number;
};

export type PreviousWorkSnapshot = {
  started_at?: string;
  started_by?: string;
  done_at?: string;
  done_by?: string;
  clock_in?: AppointmentClockData;
  clock_out?: AppointmentClockData;
  tasks: { id: string; task_id: string; task_name: string; task_category_name: string; done: boolean }[];
  photos: { id: string; workflow_id: string; workflow_name: string; workflow_description: string | null; image: string; is_after: boolean; meta_data?: any }[];
  // Ad-hoc extra photos captured during that work session (goback clears
  // meta_data.extra_photos, so the snapshot is the only remaining reference
  // to these storage files).
  extra_photos?: AppointmentExtraPhoto[];
};

// Ad-hoc photos the worker adds beyond the required service_workflows slots,
// stored in appointments.meta_data.extra_photos (no dedicated table).
export type AppointmentExtraPhoto = {
  url: string;
  is_after: boolean;
  taken_at: string | null;
  user: string | null;
};

export function getExtraPhotos(
  metaData: AppointmentMetaData | null | undefined,
  isAfter: boolean
): AppointmentExtraPhoto[] {
  const raw = metaData?.extra_photos;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (photo): photo is AppointmentExtraPhoto =>
      Boolean(photo) && typeof (photo as AppointmentExtraPhoto).url === "string" &&
      (photo as AppointmentExtraPhoto).is_after === isAfter
  );
}

export type AppointmentMetaData = {
  done_at?: string; // ISO timestamp when status changed to "done"
  goback_at?: string; // ISO timestamp when status changed to "goback"
  goback_reasons?: string | null; // Reason for goback
  cancelled_at?: string; // ISO timestamp when status changed to "cancelled"
  cancelled_by?: string; // User ID who cancelled the appointment
  cancellation_notes?: string | null; // Optional cancellation notes
  previous_work?: PreviousWorkSnapshot[]; // Array of work snapshots from each goback cycle
  reassignment_requested_by?: string; // User ID who requested reassignment
  reassignment_request_reason?: string; // Reason for reassignment request
  reassignment_requested_at?: string; // ISO timestamp when reassignment was requested

  // Clock-in/out tracking (nested objects with photo proof)
  clock_in?: AppointmentClockData;
  clock_out?: AppointmentClockData;

  // "Start Work" phase marker: set after the before-photo review, locking the
  // before photos and unlocking the checklist. Cleared on goback.
  work_started_at?: string;
  work_started_by?: string;
  extra_photos?: AppointmentExtraPhoto[];

  [key: string]: unknown;
};

export enum appointment_status {
  pending = "pending",
  inprogress = "inprogress",
  assigned = "assigned",
  accepted = "accepted",
  goback = "goback",
  rescheduled = "rescheduled",
  inspected = "inspected",
  done = "done",
  cancelled = "cancelled",
  completed = "completed",
}

export enum individual_payment_status {
  pending = "pending",
  partial = "partial",
  paid = "paid",
}

export enum subs_payment_status {
  pending = "pending",
  partial = "partial",
  paid = "paid",
}

export interface Appointment {
  id: uuid;
  client: uuid;
  vendor: uuid;
  property: uuid;
  service: uuid;
  unit: uuid;
  date: string;
  time: string;
  final_price: number;
  status: appointment_status;
  individual_payment: number;
  individual_payment_status: individual_payment_status;
  assigned_organization: uuid;
  subs_payment: number;
  subs_payment_status: subs_payment_status;
  assigned_user: uuid;
  purchase_order: PurchaseOrder;
  meta_data: AppointmentMetaData;
  work_order_notes: string;
  created_at: timestamp;
  updated_at: timestamp;
}
